import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import { GuestEmailCapture } from "../components/GuestEmailCapture";
import { ExitIntentCapture } from "../components/ExitIntentCapture";
import { getSpinDiscount } from "../components/SpinWheel";

export const CartPage = () => {
  const { items, updateQty, removeItem, subtotal, checkout, checkingOut } = useCart();
  const spinCode = getSpinDiscount();

  const doCheckout = async () => {
    try {
      track("begin_checkout", { value: subtotal, currency: "USD", items: items.length });
      const url = await checkout();
      if (url) window.location.href = url;
      else toast.error("Your cart is empty.");
    } catch { toast.error("Checkout is temporarily unavailable. Please try again."); }
  };

  if (!items.length) return (
    <div className="max-w-3xl mx-auto py-24 px-4 text-center" data-testid="cart-empty">
      <ShoppingBag className="w-14 h-14 mx-auto text-black/20" />
      <h1 className="text-2xl font-black font-display mt-4">Your cart is empty</h1>
      <p className="text-muted-foreground mt-2">Discover trending products, big sales, and premium picks.</p>
      <Link to="/shop" className="inline-flex items-center gap-2 bg-taven-black text-white px-7 py-3.5 font-bold uppercase tracking-wider text-sm mt-6 hover:bg-taven-orange transition-colors">Continue Shopping <ArrowRight className="w-4 h-4" /></Link>
    </div>
  );

  return (
    <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-8" data-testid="cart-page">
      <ExitIntentCapture />
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter font-display mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="divide-y divide-black/10 border-y border-black/10">
          {items.map((it) => (
            <div key={it.variantId} className="flex gap-4 py-5" data-testid={`cart-item-${it.handle}`}>
              <Link to={`/product/${it.handle}`} className="w-24 h-28 bg-taven-gray shrink-0 overflow-hidden">{it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}</Link>
              <div className="flex-1">
                <Link to={`/product/${it.handle}`} className="text-sm font-semibold line-clamp-2 hover:text-taven-gold">{it.title}</Link>
                <p className="text-lg font-black mt-1">{money(it.price, it.currency)}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-black/20">
                    <button onClick={() => updateQty(it.variantId, it.quantity-1)} className="p-2 hover:bg-taven-gray" data-testid={`cart-dec-${it.handle}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="px-4 text-sm font-bold">{it.quantity}</span>
                    <button onClick={() => updateQty(it.variantId, it.quantity+1)} className="p-2 hover:bg-taven-gray" data-testid={`cart-inc-${it.handle}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(it.variantId)} className="text-xs text-muted-foreground hover:text-taven-red flex items-center gap-1" data-testid={`cart-remove-${it.handle}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                </div>
              </div>
              <div className="text-right font-black">{money(it.price * it.quantity, it.currency)}</div>
            </div>
          ))}
        </div>

        <aside className="bg-white border border-black/10 p-6 h-fit lg:sticky lg:top-28">
          <h3 className="text-lg font-black font-display mb-4">Order Summary</h3>
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Subtotal</span><span className="font-bold">{money(subtotal)}</span></div>
          {spinCode && (
            <div className="flex justify-between text-sm mb-2 text-taven-gold font-bold" data-testid="cart-discount-applied">
              <span>Spin &amp; Save ({spinCode})</span><span>−10% at checkout</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-4"><Truck className="w-3.5 h-3.5" /> Shipping &amp; taxes calculated at secure checkout.</p>
          <div className="mb-4"><GuestEmailCapture /></div>
          <button onClick={doCheckout} disabled={checkingOut} data-testid="checkout-button" className="w-full bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">{checkingOut ? "Preparing..." : (<>Secure Checkout <ArrowRight className="w-4 h-4" /></>)}</button>
          <Link to="/shop" className="block text-center text-xs font-bold uppercase tracking-wider mt-4 hover:text-taven-gold" data-testid="continue-shopping">Continue Shopping</Link>
        </aside>
      </div>
    </div>
  );
};

export default CartPage;
